const MAX_POLYGON_VERTICES = 64;
const MAX_MAPPED_COORDINATE = 1e6;
const HOMOGRAPHY_EPSILON = 1e-10;
const CAPSULE_SEGMENTS = 12;
const CIRCLE_SEGMENTS = CAPSULE_SEGMENTS * 2;

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function validPath(path) {
  if (!Array.isArray(path) || path.length < 6 || path.length % 2 !== 0
    || path.length / 2 > MAX_POLYGON_VERTICES) return false;
  for (let index = 0; index < path.length; index++) {
    if (!Object.prototype.hasOwnProperty.call(path, index) || !Number.isFinite(path[index])) return false;
  }
  return true;
}

function normalizeStrokes(values, depth = 0) {
  if (!Array.isArray(values) || depth > 32) return undefined;
  const strokes = [];
  for (const stroke of values) {
    if (!isPlainObject(stroke) || !Array.isArray(stroke.paths)
      || (stroke.mode != null && !['erase', 'restore'].includes(stroke.mode))) return undefined;
    const paths = [];
    for (const path of stroke.paths) {
      if (!validPath(path)) return undefined;
      paths.push(path.slice());
    }
    const result = { paths };
    if (stroke.mode === 'restore') result.mode = 'restore';
    if (stroke.layer != null) {
      result.layer = normalizeStrokes(stroke.layer, depth + 1);
      if (!result.layer) return undefined;
    }
    strokes.push(result);
  }
  return strokes;
}

function normalizeEraser(value) {
  try {
    if (!isPlainObject(value) || value.version !== 1 || !Array.isArray(value.strokes)) return undefined;
    const strokes = normalizeStrokes(value.strokes);
    if (!strokes) return undefined;
    return { version: 1, strokes };
  } catch (error) {
    // Accessors and proxies should not be able to make persisted data unsafe.
    return undefined;
  }
}

function hasEraser(value) {
  const eraser = normalizeEraser(value);
  return Boolean(eraser && strokesHavePaths(eraser.strokes));
}

function strokesHavePaths(strokes, depth = 0) {
  if (!Array.isArray(strokes) || depth > 32) return false;
  return strokes.some(stroke => stroke?.paths?.length > 0
    || (Array.isArray(stroke?.layer) && strokesHavePaths(stroke.layer, depth + 1)));
}

function mapEraserPaths(eraser, mapPath) {
  const result = normalizeEraser(eraser);
  if (!result) return result;
  const visit = strokes => {
    for (const stroke of strokes) {
      stroke.paths = stroke.paths.map(mapPath).filter(path => path && validPath(path));
      if (stroke.layer) visit(stroke.layer);
    }
  };
  visit(result.strokes);
  return result;
}

function readPoint(point) {
  if (Array.isArray(point) && point.length >= 2
    && Number.isFinite(point[0]) && Number.isFinite(point[1])) {
    return { x: point[0], y: point[1] };
  }
  if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
    return { x: point.x, y: point.y };
  }
  return null;
}

function mapCapsulePoint(mapToSource, point) {
  const mapped = mapToSource.length >= 2
    ? mapToSource(point.x, point.y)
    : mapToSource(point);
  const result = readPoint(mapped);
  if (!result || Math.abs(result.x) > MAX_MAPPED_COORDINATE
    || Math.abs(result.y) > MAX_MAPPED_COORDINATE) return null;
  return result;
}

function capsulePolygon(fromScreen, toScreen, radius, mapToSource) {
  const from = readPoint(fromScreen);
  const to = readPoint(toScreen);
  if (!from || !to || !Number.isFinite(radius) || radius <= 0
    || typeof mapToSource !== 'function') return null;

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  const screenPoints = [];

  if (length <= 1e-10) {
    for (let index = 0; index < CIRCLE_SEGMENTS; index++) {
      const angle = index / CIRCLE_SEGMENTS * Math.PI * 2;
      screenPoints.push({
        x: from.x + Math.cos(angle) * radius,
        y: from.y + Math.sin(angle) * radius,
      });
    }
  } else {
    const angle = Math.atan2(dy, dx);
    for (let index = 0; index <= CAPSULE_SEGMENTS; index++) {
      const theta = angle - Math.PI / 2 + index / CAPSULE_SEGMENTS * Math.PI;
      screenPoints.push({
        x: to.x + Math.cos(theta) * radius,
        y: to.y + Math.sin(theta) * radius,
      });
    }
    for (let index = 0; index <= CAPSULE_SEGMENTS; index++) {
      const theta = angle + Math.PI / 2 + index / CAPSULE_SEGMENTS * Math.PI;
      screenPoints.push({
        x: from.x + Math.cos(theta) * radius,
        y: from.y + Math.sin(theta) * radius,
      });
    }
  }

  try {
    const polygon = [];
    for (const point of screenPoints) {
      const mapped = mapCapsulePoint(mapToSource, point);
      if (!mapped) return null;
      polygon.push(mapped.x, mapped.y);
    }
    return polygon.length / 2 <= MAX_POLYGON_VERTICES ? polygon : null;
  } catch (error) {
    return null;
  }
}

function inverseHomographyPoint(h, x, y) {
  if (!Array.isArray(h) || h.length !== 8
    || !h.every(Number.isFinite) || !Number.isFinite(x) || !Number.isFinite(y)) return null;

  const [a, b, c, d, e, f, g, i] = h;
  const A = e - f * i;
  const B = c * i - b;
  const C = b * f - c * e;
  const D = f * g - d;
  const E = a - c * g;
  const F = c * d - a * f;
  const G = d * i - e * g;
  const H = b * g - a * i;
  const I = a * e - b * d;
  const determinant = a * A + b * D + c * G;
  if (!Number.isFinite(determinant) || Math.abs(determinant) <= HOMOGRAPHY_EPSILON) return null;

  const denominator = (G * x + H * y + I) / determinant;
  if (!Number.isFinite(denominator) || Math.abs(denominator) <= HOMOGRAPHY_EPSILON) return null;
  const sourceX = ((A * x + B * y + C) / determinant) / denominator;
  const sourceY = ((D * x + E * y + F) / determinant) / denominator;
  if (!Number.isFinite(sourceX) || !Number.isFinite(sourceY)
    || Math.abs(sourceX) > MAX_MAPPED_COORDINATE
    || Math.abs(sourceY) > MAX_MAPPED_COORDINATE) return null;
  return { x: sourceX, y: sourceY };
}

function applyErasePaths(ctx, paths, sourceRect = { x: 0, y: 0, width: 1, height: 1 }) {
  if (!ctx || !Array.isArray(paths) || !isPlainObject(sourceRect)
    || ![sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height].every(Number.isFinite)
    || sourceRect.width <= 0 || sourceRect.height <= 0) return false;
  const validPaths = paths.filter(validPath);
  if (validPaths.length === 0) return false;

  ctx.save();
  try {
    // sourceRect is already expressed in bitmap pixels. Konva.toCanvas leaves
    // its pixel-ratio transform on the raw 2D context; applying it here again
    // makes holes move/vanish whenever zoom changes raster resolution.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    for (const path of validPaths) {
      ctx.beginPath();
      ctx.moveTo(sourceRect.x + path[0] * sourceRect.width,
        sourceRect.y + path[1] * sourceRect.height);
      for (let index = 2; index < path.length; index += 2) {
        ctx.lineTo(sourceRect.x + path[index] * sourceRect.width,
          sourceRect.y + path[index + 1] * sourceRect.height);
      }
      ctx.closePath();
      ctx.fill();
    }
  } finally {
    ctx.restore();
  }
  return true;
}

function applyEraser(ctx, eraser, sourceRect = { x: 0, y: 0, width: 1, height: 1 }) {
  if (!ctx || !isPlainObject(sourceRect)
    || ![sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height].every(Number.isFinite)
    || sourceRect.width <= 0 || sourceRect.height <= 0) return false;
  const normalized = normalizeEraser(eraser);
  if (!normalized) return false;
  if (!normalized.strokes.some(stroke => stroke.mode === 'restore' || stroke.layer)) {
    return applyErasePaths(ctx, normalized.strokes.flatMap(stroke => stroke.paths), sourceRect);
  }
  // Paint deletion coverage separately from source pixels. Repair removes
  // coverage, never paints opaque colour over the object's original alpha.
  const owner = ctx.canvas?.ownerDocument;
  if (!owner) return false;
  const buildMask = strokes => {
    const canvas = owner.createElement('canvas');
    canvas.width = ctx.canvas.width; canvas.height = ctx.canvas.height;
    const mask = canvas.getContext('2d');
    for (const stroke of strokes) {
      if (stroke.layer) {
        mask.globalCompositeOperation = 'source-over';
        mask.drawImage(buildMask(stroke.layer), 0, 0);
      }
      if (stroke.mode === 'restore') {
        applyErasePaths(mask, stroke.paths, sourceRect);
      } else {
        mask.save();
        mask.globalCompositeOperation = 'source-over';
        mask.fillStyle = '#fff';
        paintPaths(mask, stroke.paths, sourceRect || { x: 0, y: 0, width: 1, height: 1 });
        mask.restore();
      }
    }
    return canvas;
  };
  const mask = buildMask(normalized.strokes);
  ctx.save();
  try {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(mask, 0, 0);
  } finally { ctx.restore(); }
  return true;
}

function paintPaths(ctx, paths, rect) {
  for (const path of paths) {
    ctx.beginPath();
    ctx.moveTo(rect.x + path[0] * rect.width, rect.y + path[1] * rect.height);
    for (let i = 2; i < path.length; i += 2) ctx.lineTo(rect.x + path[i] * rect.width, rect.y + path[i + 1] * rect.height);
    ctx.closePath();
    ctx.fill();
  }
}

function applyRepairPaths(ctx, paths, sourceRect, original) {
  if (!ctx || !original || !Array.isArray(paths) || !isPlainObject(sourceRect)
    || ![sourceRect.x, sourceRect.y, sourceRect.width, sourceRect.height].every(Number.isFinite)
    || sourceRect.width <= 0 || sourceRect.height <= 0) return false;
  const valid = paths.filter(validPath);
  if (!valid.length) return false;
  ctx.save();
  try {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    for (const path of valid) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(sourceRect.x + path[0] * sourceRect.width, sourceRect.y + path[1] * sourceRect.height);
      for (let i = 2; i < path.length; i += 2) ctx.lineTo(sourceRect.x + path[i] * sourceRect.width, sourceRect.y + path[i + 1] * sourceRect.height);
      ctx.closePath();
      ctx.clip();
      ctx.globalCompositeOperation = 'copy';
      ctx.drawImage(original, 0, 0);
      ctx.restore();
    }
  } finally { ctx.restore(); }
  return true;
}

module.exports = {
  normalizeEraser,
  hasEraser,
  capsulePolygon,
  inverseHomographyPoint,
  applyErasePaths,
  applyEraser,
  applyRepairPaths,
  mapEraserPaths,
  strokesHavePaths,
};